import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const relatedArticles = article ? getRelatedArticles(article) : [];

  if (!article) {
    return (
      <>
        <SEOHead title="内容未找到" description="该内容不存在或已被移除。" />
        <main className="pt-20 pb-10">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </span>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-4">该内容不存在或已被移除</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
      </>
    );
  }

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '久久视频',
      url: 'https://jiujiu.video',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://jiujiu.video/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`久久视频,${article.tags.join(',')},${article.category}`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <main className="pt-20 pb-10">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <article className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-2.5 py-1 text-xs bg-primary-50 text-primary-600 rounded-full hover:bg-primary-100 transition-colors"
                >
                  {article.category}
                </Link>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 text-xs bg-background-100 text-foreground-500 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {formatViews(article.views)}次观看
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line"></i> {formatViews(article.likes)}赞
                </span>
              </div>
            </div>

            <div className="relative aspect-video rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 久久视频高清在线观看`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 flex items-center gap-3">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-white cursor-pointer hover:bg-primary-600 transition-colors">
                  <i className="ri-play-fill text-xl"></i>
                </span>
                <span className="text-white/80 text-sm">{article.duration}</span>
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors text-sm cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line"></i> 点赞 ({formatViews(article.likes)})
                  </button>
                  <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors text-sm cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line"></i> 分享
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-4xl mx-auto mt-10">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedArticles.map((related) => (
                  <VideoCard key={related.id} article={related} variant="default" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}